import React from "react";
import { Button } from "@/components/ui/button";
import BasePopup from "./BasePopup";

const PrivacyPolicyPopup = ({ isOpen, onClose }) => {
    return (
        <BasePopup isOpen={isOpen} onClose={onClose} aria-labelledby="privacy-policy-title">
            <div className="text-center mb-6">
                <h3 id="privacy-policy-title" className="text-xl font-bold text-gray-900">
                    Privacy Policy
                </h3>
                <p className="text-gray-600 mt-2">Last updated: April 2025</p>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 mb-5 text-sm max-h-72 overflow-y-auto">
                <div className="text-gray-700 space-y-4">
                    <p>
                        Your privacy is important to us. This Privacy Policy explains how JobPool.ai collects, uses, stores, and protects your personal information.
                    </p>

                    <h4 className="font-semibold text-gray-800">1. Information We Collect</h4>
                    <p>
                        We collect personal information you provide directly, such as your name, email address, résumé, job preferences, and other content you submit.
                        We may also collect usage data like IP address, browser type, and site interactions. Resumes are securely stored using Google Cloud Storage.
                    </p>

                    <h4 className="font-semibold text-gray-800">2. How We Use Your Information</h4>
                    <p>
                        Your data helps us match you with relevant jobs, personalize your experience, analyze usage trends, and improve our services.
                        We may also send you important notifications or updates.
                    </p>

                    <h4 className="font-semibold text-gray-800">3. Information Sharing and Data Security</h4>
                    <p>
                        We prioritize the security and confidentiality of your personal information.
                        We do not sell, trade, or otherwise share your personal data with third parties without your explicit consent.
                        We also do not sell your personal information to third parties, including under the California Consumer Privacy Act (CCPA).
                    </p>
                    <p>
                        We share your information only with trusted service providers necessary for delivering our services, including:
                    </p>
                    <ul className="list-disc list-inside ml-4 text-gray-700">
                        <li><strong>Google Cloud Storage</strong> (secure cloud storage)</li>
                        <li><strong>OpenAI/Gemini</strong> (AI-based job ranking)</li>
                        <li><strong>Postmark</strong> (email delivery services)</li>
                    </ul>
                    <p>
                        These providers are contractually obligated to safeguard your information and may not use it for any purpose other than supporting our operations.
                        We implement reasonable technical and organizational measures to protect your data. However, no method of transmission or storage is 100% secure.
                    </p>

                    <h4 className="font-semibold text-gray-800">4. Legal Compliance</h4>
                    <p>
                        We may disclose your information if legally required, to enforce our terms, protect users, or address fraud or abuse.
                    </p>

                    <h4 className="font-semibold text-gray-800">5. Data Retention</h4>
                    <p>
                        We retain your data only as long as needed for service delivery and legal compliance. You may request deletion at any time.
                        Some backups may retain anonymized data for a limited time.
                    </p>

                    <h4 className="font-semibold text-gray-800">6. Your Rights</h4>
                    <p>
                        Depending on your location, you may request access, correction, deletion, or restriction of your personal data.
                        You may also withdraw your consent at any time.
                    </p>

                    <h4 className="font-semibold text-gray-800">7. International Users</h4>
                    <p>
                        By using our platform from outside the U.S., you consent to your information being transferred to, stored, and processed in the United States.
                    </p>

                    <h4 className="font-semibold text-gray-800">8. Updates to This Policy</h4>
                    <p>
                        We may update this policy from time to time. All changes will be posted here with the updated date.
                        Continued use of our services constitutes acceptance of the revised policy.
                    </p>

                    <h4 className="font-semibold text-gray-800">9. Contact</h4>
                    <p>
                        If you have any questions about this policy or your data, please contact us at{" "}
                        <a href="mailto:contact@jobpool.ai" className="text-blue-600 underline">
                            contact@jobpool.ai
                        </a>.
                    </p>
                </div>
            </div>

            <Button
                className="w-full bg-blue-600 hover:bg-blue-700 h-10 text-sm font-medium rounded-lg"
                onClick={onClose}
            >
                I Understand
            </Button>
        </BasePopup>
    );
};

export default PrivacyPolicyPopup;